/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {DiagnosticTileSeverity, getDiagnosticTileSeverityVisual} from './diagnosticTileConfig';

export interface DiagnosticTileProps {
  label: string;
  value: string;
  unit?: string;
  severity: DiagnosticTileSeverity;
}

interface DiagnosticTileStyleProps {
  background: string;
  border: string;
  labelColor: string;
  hasIcon: boolean;
}

// a percentage sits against its figure (`9%`); other units follow a space
// (`12.7 V`, `1234 mi`) ...
const formatUnit = (unit: string): string => (unit === '%' ? unit : ` ${unit}`);

const useStyles = makeStyles(() => ({
  tile : {
    boxSizing    : 'border-box',
    display      : 'flex',
    alignItems   : 'center',
    width        : '130.67px',
    height       : '64px',
    padding      : '8px',
    gap          : '16px',
    borderRadius : '8px',
    background   : ({background}: DiagnosticTileStyleProps) => background,
    border       : ({border}: DiagnosticTileStyleProps) => border
  },
  contents : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    gap           : '8px',
    width         : '114.67px',
    minWidth      : 0
  },
  labelRow : {
    display        : 'flex',
    alignItems     : 'center',
    width          : '100%',
    justifyContent : ({hasIcon}: DiagnosticTileStyleProps) => (hasIcon ? 'space-between' : 'flex-start'),
    gap            : ({hasIcon}: DiagnosticTileStyleProps) => (hasIcon ? '8px' : '10px')
  },
  label : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    whiteSpace    : 'nowrap',
    color         : ({labelColor}: DiagnosticTileStyleProps) => labelColor
  },
  value : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '18px',
    lineHeight : '22px',
    color      : dlsColor('grey2')
  },
  // the measuring unit (V, mi, %) is smaller than the figure it follows ...
  unit : {
    fontSize      : '12px',
    fontWeight    : 600,
    letterSpacing : '0px',
    lineHeight    : '22px',
    verticalAlign : 'middle'
  }
}));

/**
 * DiagnosticTile renders a single ION-7462 Diagnostic Tile - an uppercase
 * label, its value (with an optional unit suffix, in Inter Semi Bold 12px
 * beside the larger figure), and a severity-driven
 * visual per `diagnosticTileConfig`. Per the Figma "Diagnostic Tiles by
 * Severity" export, `good` and `warning` share the same white/grey-bordered
 * tile - only `critical` recolors the tile background, border, and label
 * text. `warning` and `critical` additionally show a small severity icon
 * to the right of the label (an orange triangle / a red exception glyph
 * respectively); `good` shows no icon at all. The value text is always
 * Grey 2, even when critical.
 *
 * ```typescript
 *
 *   import {DiagnosticTile} from
 *     '@ion/dls/src/components/assetdetails/current/DiagnosticTile';
 *   ...
 *
 *   <DiagnosticTile label='FUEL LEVEL'
 *     value='9'
 *     unit='%'
 *     severity='critical' />
 *
 * ```
 *
 */
export const DiagnosticTile = ({
  label,
  value,
  unit,
  severity
}: DiagnosticTileProps) => {
  const visual  = getDiagnosticTileSeverityVisual(severity);
  const hasIcon = Boolean(visual.Icon);
  const classes = useStyles({
    background : visual.background,
    border     : visual.border,
    labelColor : visual.labelColor,
    hasIcon
  });
  const Icon = visual.Icon;
  return (
    <div className={classes.tile}>
      <div className={classes.contents}>
        <div className={classes.labelRow}>
          <span className={classes.label}>
            {label}
          </span>
          {hasIcon && <Icon size={18} color={visual.iconColor} />}
        </div>
        <span className={classes.value}>
          {value}
          {unit && (
            <span className={classes.unit}>
              {formatUnit(unit)}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default memo(DiagnosticTile);
